import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import DeviationProductIntegration from './components/DeviationProductIntegration';
import DeviationCriticalityAndCauses from './components/DeviationCriticalityAndCauses';
import DeviationActionsIntegration from './components/DeviationActionsIntegration';
import DeviationImplementationStatusIntegration from './components/DeviationImplementationStatusIntegration';
import DeviationReferenceDocument from './components/DeviationReferenceDocument';
import DeviationRiskAssessmentSection from './components/DeviationRiskAssessmentSection';
import ImplementationEvaluationPeriodBadges from './components/ImplementationEvaluationPeriodBadges';
import CustomDeviationSplitView from './CustomDeviationSplitView';

const deviationTrackingLabels = getLabels('Deviation');
const deviationTrackingBase = createTrackingConfig('Deviation');

export const deviationConfig = {
  entityName: 'Deviation',
  
  // Entity information for file attachments
  entityTypeId: 8, // Deviation entity type ID (adjust if needed)
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/deviation',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      duplicate: (id) => `/${id}/duplicate`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      departments: '/department',
      users: '/users',
      deviationStatuses: '/deviation-status',
      deviationActionTypes: '/deviation-action-type',
      deviationCriticality: '/deviation-criticality',
      deviationFinalDisposition: '/deviation-final-disposition',
      priorities: '/priority',
      customers: '/customer',
      rootCauses: '/deviation-root-cause',
      riskAssessments: '/risk-assessment',
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/deviation',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '50%', // 50% of table width (similar to complaints)
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
    showDuplicateButton: true // Show duplicate button in table actions
  },

  // Custom Split View Panel configuration for Deviation module
  customSplitViewPanel: {
    enabled: true,
    component: CustomDeviationSplitView  // Use the custom Deviation split view component
  },
  
  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality
    masterEntityTypeId: 8, // Deviation master entity type ID for audit logs
    approvalRequests: true, // Enable approval request functionality
  },
  
  // Custom handlers for deviation-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in deviationPage
    onDelete: null, // Will be defined in deviationPage
    onLoad: null, // Will be defined in deviationPage
    onFormOpen: null, // Will be defined in deviationPage
  },

  // Display Configuration
  displayName: 'Deviation Management',
  description: 'Quality deviation management and investigation system',
  icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z',

  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'reportedBy', 'discoveredBy',
    'status', 'criticality', 'finalDisposition',
    'closedBy', 'departmentHeadInvestigationBy', 'qaManagerInvestigationBy', 'implementationEvaluationBy', 'riskAssessment',
    'attachments', 'timelineEntries', 'approvalSteps',
    'deviationActions', 'linkedCAPA', 'complaints',
    'rootCauses', // Will be mapped to rootCauseIds
    'createdBy', 'updatedBy', 'createdById', 'updatedById', 'createdAt', 'updatedAt',
    'fileAttachments', 'fileAttachmentCount', // File attachments handled separately
    // Possible Causes fields (removed from edit form)
    'humanFactor', 'humanFactorNote', 'equipment', 'equipmentNote', 'methods', 'methodsNote',
    'materials', 'materialsNote', 'supplier', 'supplierNote', 'facility', 'facilityNote'
  ],
  
  // Fields to keep even if they're in fieldsToRemove (for custom handling)
  fieldsToKeep: ['products'],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'code',
      label: 'Deviation Code',
      type: 'text',
      required: true,
      placeholder: 'Enter deviation code',
      table: { header: 'Code', width: '130px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 50,
        message: 'Deviation code is required and must not exceed 50 characters'
      }
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter deviation title',
      table: { 
        header: 'Title', 
        width: '200px', 
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 50
      },
      filterable: true,
      validation: {
        required: true,
        maxLength: 200,
        message: 'Title is required and must not exceed 200 characters'
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter deviation description',
      rows: 4,
      table: { 
        header: 'Description', 
        width: '300px', 
        sortable: true,
        cellRenderer: 'lineClamp',
        maxLines: 3 // Show up to 3 lines, then truncate
      },
      filterable: false,
      validation: {
        required: true,
        maxLength: 2000,
        message: 'Description is required and must not exceed 2000 characters'
      }
    },
    {
      name: 'discoveredAt',
      label: 'Discovered Date',
      type: 'date',
      required: true,
      table: {
        header: 'Discovered',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date',
      validation: {
        required: true,
        message: 'Discovery date is required'
      }
    },
    {
      name: 'reportedAt',
      label: 'Reported Date',
      type: 'date',
      required: false,
      table: {
        header: 'Reported',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date',
      validation: {
        message: 'Please enter a valid reported date'
      }
    },
    {
      name: 'discoveredById',
      label: 'Discovered By Depositor',
      type: 'select',
      required: false,
      lookupEndpoint: 'customers',
      options: [],
      table: {
        header: 'Discovered By',
        width: '150px',
        sortable: true,
        displayField: 'discoveredBy'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid customer'
      }
    },
    {
      name: 'reportedByName',
      label: 'Reported By',
      type: 'text',
      required: false,
      placeholder: 'Enter reported by name',
      table: {
        header: 'Reported By',
        width: '150px',
        sortable: true
      },
      filterable: true,
      filterType: 'text',
      validation: {
        maxLength: 200,
        message: 'Reported By must not exceed 200 characters'
      }
    },
    {
      name: 'rootCauseIds',
      label: 'Cause Categorization',
      type: 'multiSelect',
      required: false,
      lookupEndpoint: 'rootCauses',
      options: [],
      table: false,
      filterable: false,
      validation: {
        message: 'Please select valid cause categorizations'
      }
    },
    {
      name: 'rootCauseNote',
      label: 'Cause Categorization Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter cause categorization analysis notes',
      rows: 3,
      table: false,
      filterable: false,
      validation: {
        maxLength: 4000,
        message: 'Cause categorization note must not exceed 4000 characters'
      }
    },
    // ΥΠ. ΤΜΗΜΑΤΟΣ (Department Head) Investigation
    {
      name: 'departmentHeadInvestigation',
      label: 'Department Head Date',
      type: 'date',
      required: false,
      table: false,
      filterable: false,
      validation: {
        message: 'Please enter a valid investigation date'
      }
    },
    {
      name: 'departmentHeadInvestigationById',
      label: 'Department Head By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: false,
      filterable: false,
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    // Υ.Δ.Π (Quality Assurance Manager) Investigation
    {
      name: 'qaManagerInvestigation',
      label: 'QA Manager Date',
      type: 'date',
      required: false,
      table: false,
      filterable: false,
      validation: {
        message: 'Please enter a valid investigation date'
      }
    },
    {
      name: 'qaManagerInvestigationById',
      label: 'QA Manager By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: false,
      filterable: false,
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'criticalityId',
      label: 'Criticality Categorization',
      type: 'select',
      required: false,
      lookupEndpoint: 'deviationCriticality',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid criticality level'
      },
      table: {
        header: 'Criticality',
        width: '120px',
        sortable: true,
        displayField: 'criticality',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Impact Assessment
    {
      name: 'customerImpact',
      label: 'Notification of Customer',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: { header: 'Notification of Customer', width: '130px', sortable: true, cellRenderer: 'booleanBadge' },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select customer notification status'
      }
    },
    {
      name: 'impactOnProduct',
      label: 'Impact on Product',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'impactOnOtherBatches',
      label: 'Impact on Other Batches',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'impactOnOtherProducts',
      label: 'Impact on Other Products',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'impactOnOtherSystems',
      label: 'Impact on Other Systems',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'customerImpactNote',
      label: 'Notification of Customer Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter customer notification details',
      rows: 2,
      table: false,
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Notification of customer note must not exceed 1000 characters'
      }
    },
    {
      name: 'impactOnProductNote',
      label: 'Impact on Product Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter impact on product details',
      rows: 2,
      table: false,
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Impact on product note must not exceed 1000 characters'
      }
    },
    {
      name: 'impactOnOtherBatchesNote',
      label: 'Impact on Other Batches Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter impact on other batches details',
      rows: 2,
      table: false,
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Impact on other batches note must not exceed 1000 characters'
      }
    },
    {
      name: 'impactOnOtherProductsNote',
      label: 'Impact on Other Products Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter impact on other products details',
      rows: 2,
      table: false,
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Impact on other products note must not exceed 1000 characters'
      }
    },
    {
      name: 'impactOnOtherSystemsNote',
      label: 'Impact on Other Systems Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter impact on other systems details',
      rows: 2,
      table: false,
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Impact on other systems note must not exceed 1000 characters'
      }
    },
    {
      name: 'finalDispositionId',
      label: 'Final Disposition',
      type: 'select',
      required: false,
      lookupEndpoint: 'deviationFinalDisposition',
      options: [],
      validation: {
        integer: true,
        message: 'Please select a valid final disposition'
      },
      table: {
        header: 'Final Disposition',
        width: '150px',
        sortable: true,
        displayField: 'finalDisposition',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    {
      name: 'finalDispositionOtherNote',
      label: 'Other Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter other final disposition details',
      rows: 2,
      table: false,
      filterable: false,
      dependsOn: {
        field: 'finalDispositionId',
        value: (formData, lookupData) => {
          // Check if the selected finalDispositionId corresponds to "Other (specify)"
          const finalDispositionId = formData.finalDispositionId;
          if (!finalDispositionId) return false;
          
          // Check if we have the finalDisposition object with name
          if (formData.finalDisposition && formData.finalDisposition.name) {
            const name = formData.finalDisposition.name.toLowerCase();
            return name.includes('other') || name.includes('specify');
          }
          
          // Check lookup data if available
          if (lookupData?.deviationFinalDisposition) {
            const otherDisposition = lookupData.deviationFinalDisposition.find(
              d => d.id === finalDispositionId && d.name && 
              (d.name.toLowerCase().includes('other') || d.name.toLowerCase().includes('specify'))
            );
            return !!otherDisposition;
          }
          
          return false;
        }
      },
      validation: {
        maxLength: 2000,
        message: 'Other note must not exceed 2000 characters'
      }
    },
    // Risk Assessment
    {
      name: 'riskAssessmentRequired',
      label: 'Risk Assessment Required',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'riskAssessmentId',
      label: 'Risk Assessment',
      type: 'select',
      required: false,
      lookupEndpoint: 'riskAssessments',
      labelField: 'code',
      options: [],
      table: {
        header: 'Risk Assessment',
        width: '150px',
        sortable: true,
        displayField: 'riskAssessment'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid risk assessment'
      }
    },
    {
      name: 'riskAssessmentCompletedAt',
      label: 'RA Completed At',
      type: 'date',
      required: false,
      table: {
        header: 'RA Completed',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date'
    },
    // Implementation
    {
      name: 'implementation',
      label: 'All required actions have been completed',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'implementationCompletedAt',
      label: 'Implementation Completed At',
      type: 'date',
      required: false,
      table: {
        header: 'Implementation Completed',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date'
    },
    // Implementation Evaluation
    {
      name: 'implementationEvaluation',
      label: 'Implementation Evaluation',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: false,
      filterable: false
    },
    {
      name: 'implementationEvaluationNote',
      label: 'Implementation Evaluation Note',
      type: 'textarea',
      required: false,
      placeholder: 'Enter implementation evaluation notes',
      rows: 1,
      table: false,
      filterable: false,
      validation: {
        maxLength: 2000,
        message: 'Implementation evaluation note must not exceed 2000 characters'
      }
    },
    {
      name: 'implementationEvaluationAt',
      label: 'Implementation Evaluation At',
      type: 'date',
      required: false,
      table: {
        header: 'Eval. At',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date'
    },
    {
      name: 'implementationEvaluationById',
      label: 'Implementation Evaluation By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: {
        header: 'Eval. By',
        width: '150px',
        sortable: true,
        displayField: 'implementationEvaluationBy'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'implementationEvaluationRequiredMonths',
      label: 'Required',
      type: 'custom',
      required: false,
      customComponent: ImplementationEvaluationPeriodBadges,
      options: [
        { value: 3, label: '3 months' },
        { value: 6, label: '6 months' },
      ],
      table: {
        header: 'Eval. Period',
        width: '120px',
        sortable: true,
        displayField: 'implementationEvaluationRequiredMonthsDisplay',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select 3 or 6 months',
      },
    },
    {
      name: 'closedAt',
      label: 'Closed At',
      type: 'date',
      required: false,
      table: {
        header: 'Closed At',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date'
    },
    {
      name: 'closedById',
      label: 'Closed By',
      type: 'select',
      required: false,
      placeholder: 'Select user',
      lookupEndpoint: 'users',
      options: [],
      table: {
        header: 'Closed By',
        width: '160px',
        sortable: true,
        displayField: 'closedBy'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'closureComments',
      label: 'Closure Comments',
      type: 'textarea',
      required: false,
      placeholder: 'Enter closure comments',
      rows: 2,
      table: false,
      filterable: false,
      validation: {
        maxLength: 2000,
        message: 'Closure comments must not exceed 2000 characters'
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      useFormFileUpload2: true, // Use the new responsive FormFileUpload_2 component
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: true,
      lookupEndpoint: 'deviationStatuses',
      options: [],
      table: {
        header: 'Status',
        width: '120px',
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid status'
      }
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: false,
      table: {
        header: 'Due Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'date'
    },
    // Products - handled by custom component
    {
      name: 'products',
      label: 'Products',
      type: 'custom',
      required: false,
      table: false,
      filterable: false
    },
    {
      name: 'referenceDocument',
      label: 'Reference Document',
      type: 'hidden',
      table: {
        header: 'Document Reference',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 40,
      },
      filterable: true,
      filterType: 'text',
    },
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    return columns;
  },

  exportFileName: 'qms_deviations',

  // Backend field mappings (flattened for table / view display)
  fieldMappings: {
    discoveredBy: 'discoveredBy.name',
    reportedBy: 'reportedByName',
    departmentHeadInvestigationBy: 'departmentHeadInvestigationBy.userDisplayName',
    qaManagerInvestigationBy: 'qaManagerInvestigationBy.userDisplayName',
    status: 'status.name',
    criticality: 'criticality.name',
    finalDisposition: 'finalDisposition.name',
    closedBy: 'closedBy.userDisplayName',
    implementationEvaluationBy: 'implementationEvaluationBy.userDisplayName',
    // Show Risk Assessment CODE (e.g. "RA-2025-01") instead of name
    riskAssessment: 'riskAssessment.code',
  },

  enrichEntity: (entity) => {
    const months = entity?.implementationEvaluationRequiredMonths;
    return {
      ...entity,
      implementationEvaluationRequiredMonthsDisplay:
        months === 3 || months === 6 ? `${months} months` : null,
    };
  },

  // Form Layout Configuration - Deviation-style sections
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'description'],
      fieldSpans: {
        code: 'md:col-span-4',
        title: 'md:col-span-8',
        description: 'md:col-span-12'
      }
    },
    {
      title: 'Products',
      icon: '📦',
      layout: 'custom',
      fields: ['products'],
      customComponent: DeviationProductIntegration
    },
    {
      title: 'Discovery & Reporting',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-4',
      fields: ['discoveredAt', 'discoveredById', 'reportedAt', 'reportedByName']
    },
    {
      title: 'Status',
      icon: '📊',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['statusId', 'dueDate']
    },
    {
      title: 'Summary of Investigation Results into the Causes of the Deviation',
      icon: '🔬',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['rootCauseIds', 'rootCauseNote', 'departmentHeadInvestigation', 'departmentHeadInvestigationById', 'qaManagerInvestigation', 'qaManagerInvestigationById'],
      fieldSpans: {
        rootCauseIds: 'md:col-span-2',
        rootCauseNote: 'md:col-span-2'
      }
    },
    {
      title: 'Assessment of Criticality and Impact',
      icon: '⚖️',
      layout: 'custom',
      customComponent: DeviationCriticalityAndCauses,
      fields: ['criticalityId', 'customerImpact', 'customerImpactNote', 'impactOnProduct', 'impactOnProductNote', 
               'impactOnOtherBatches', 'impactOnOtherBatchesNote', 'impactOnOtherProducts', 'impactOnOtherProductsNote',
               'impactOnOtherSystems', 'impactOnOtherSystemsNote']
    },
    {
      title: 'Final Product Disposition (for Product-Related Deviations)',
      icon: '📦',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['finalDispositionId', 'finalDispositionOtherNote']
    },
    {
      title: 'Risk Assessment',
      icon: '🎯',
      layout: 'custom',
      customComponent: DeviationRiskAssessmentSection,
      fields: [
        'riskAssessmentRequired',
        'riskAssessmentId',
        'riskAssessmentCompletedAt',
      ],
    },
    {
      title: 'Corrective and Preventive Actions (CAPA)',
      icon: '📋',
      layout: 'custom',
      fields: ['deviationActions'],
      customComponent: DeviationActionsIntegration
    },
    {
      title: 'Implementation',
      icon: '⚙️',
      layout: 'custom',
      fields: ['implementation', 'implementationCompletedAt', 'deviationActions'],
      customComponent: DeviationImplementationStatusIntegration
    },
    {
      title: 'Implementation Review Required',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['implementationEvaluation', 'implementationEvaluationNote', 'implementationEvaluationAt', 'implementationEvaluationById']
    },
    {
      title: 'Implementation Evaluation',
      icon: '⏳',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['implementationEvaluationRequiredMonths'],
    },
    {
      title: 'Closure',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['closedAt', 'closedById', 'closureComments']
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    },
    {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      customComponent: DeviationReferenceDocument,
      fields: [],
    }
  ],

  // Tracking Section — show audit users by display name (user objects from API / selected row)
  trackingSection: {
    ...deviationTrackingBase,
    fields: deviationTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: deviationTrackingLabels.createdDateLabel,
            userEmptyText: deviationTrackingLabels.notSpecifiedText,
            dateEmptyText: deviationTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: deviationTrackingLabels.updatedDateLabel,
              userEmptyText: deviationTrackingLabels.notModifiedText,
              dateEmptyText: deviationTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Messages
  messages: {
    loading: 'Loading deviations...',
    noData: 'No deviations found. Create your first deviation to get started.',
    createSuccess: 'Deviation created successfully!',
    updateSuccess: 'Deviation updated successfully!',
    deleteSuccess: 'Deviation deleted successfully!',
    createError: 'Failed to create deviation',
    updateError: 'Failed to update deviation',
    deleteError: 'Failed to delete deviation',
    loadError: 'Failed to load deviations',
    validationError: 'Validation failed'
  },

  // Data transformation for API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Remove fileAttachments and fileAttachmentCount from the main deviation data (handled separately)
    delete transformed.fileAttachments;
    delete transformed.fileAttachmentCount;
    
    // Remove deviationActions from the main deviation data (handled separately via APIs)
    delete transformed.deviationActions;

    // Audit: createdAt/createdById on create only; updatedAt/updatedById on update only (server / user-id header)
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Relation objects (not FKs)
    delete transformed.riskAssessment;
    delete transformed.capaEffectivenessCheck;
    delete transformed.capaEffectivenessCheckId;

    // Snapshot set at create; keep string if present
    if (transformed.referenceDocument !== undefined) {
      if (
        transformed.referenceDocument === '' ||
        transformed.referenceDocument === null
      ) {
        delete transformed.referenceDocument;
      } else {
        transformed.referenceDocument = String(transformed.referenceDocument).trim();
      }
    }

    // Convert foreign key fields to integers or explicitly set to null if cleared
    const foreignKeyFields = [
      'discoveredById', 'statusId',
      'departmentHeadInvestigationById', 'qaManagerInvestigationById',
      'criticalityId', 'finalDispositionId', 'riskAssessmentId',
      'closedById', 'implementationEvaluationById'
    ];
    foreignKeyFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          // Explicitly send null so backend can clear existing relation
          transformed[field] = null;
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Handle rootCauseIds - convert array to proper format for JSON storage
    if (transformed.rootCauseIds !== undefined) {
      if (Array.isArray(transformed.rootCauseIds) && transformed.rootCauseIds.length > 0) {
        // Ensure all values are numbers
        transformed.rootCauseIds = transformed.rootCauseIds
          .map(id => parseInt(id, 10))
          .filter(id => !isNaN(id));
      } else {
        delete transformed.rootCauseIds;
      }
    }

    // Convert dates (send null when empty so backend can clear values)
    const dateFields = ['discoveredAt', 'reportedAt', 
                        'departmentHeadInvestigation', 'qaManagerInvestigation',
                        'closedAt', 'dueDate', 
                        'riskAssessmentCompletedAt', 'implementationCompletedAt', 
                        'implementationEvaluationAt'];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) transformed[field] = dateValue; else transformed[field] = null;
        }
      }
    });

    // Implementation Evaluation period: 3 or 6 months (or clear)
    if (transformed.implementationEvaluationRequiredMonths !== undefined) {
      if (
        transformed.implementationEvaluationRequiredMonths === '' ||
        transformed.implementationEvaluationRequiredMonths === null
      ) {
        transformed.implementationEvaluationRequiredMonths = null;
      } else {
        const months = parseInt(
          transformed.implementationEvaluationRequiredMonths,
          10,
        );
        transformed.implementationEvaluationRequiredMonths = [3, 6].includes(
          months,
        )
          ? months
          : null;
      }
    }
    delete transformed.implementationEvaluationRequiredMonthsDisplay;

    return transformed;
  },

  // Validation config
  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true
  },

  // Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: false, // Show review and approval request button in deviation form
    showInDetails: true, // Show review and approval integration in deviation details tab
    requireApprovalForClose: false, // Require review and approval before closing deviation
    allowMultipleApprovals: false, // Allow multiple review and approval requests per deviation
    defaultApprovalType: 'deviation', // Default approval type when creating requests
    approvalButtonText: 'Request Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'CheckCircleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in deviation view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in deviation timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },

  // Helper functions
  isApprovalEnabled: function() {
    return this.approval && this.approval.enabled === true;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  }
};

export default deviationConfig;


